import { useState, useEffect, useContext } from "react";
import { C, API } from "./utils";
import { ThemeCtx } from "./AppShell";
import CongressPlannerInline from "./CongressPlanner";

function fmtDateRange(c) {
  if (!c) return "";
  const { STARTDATE, STARTMONTH, STARTYEAR, ENDDATE, ENDMONTH, ENDYEAR } = c;
  if (!STARTDATE) return "";
  if (STARTMONTH === ENDMONTH && STARTYEAR === ENDYEAR)
    return `${STARTDATE}–${ENDDATE} ${STARTMONTH} ${STARTYEAR}`;
  return `${STARTDATE} ${STARTMONTH} – ${ENDDATE} ${ENDMONTH} ${STARTYEAR}`;
}

function CongressCard({ congress: c, color, onSelect, onStatusToggle, isAdmin }) {
  const [hov,      setHov]      = useState(false);
  const [toggling, setToggling] = useState(false);
  const [status,   setStatus]   = useState(String(c.CONGRESS_STATUS));
  const isActive    = status === "1";
  const isCheckedIn = c.CHECKIN_STATUS === 1;
  const cardColor   = isActive ? color : "#9CA3AF";

  const toggleStatus = async(e) => {
    e.stopPropagation();
    if(toggling) return;
    setToggling(true);
    const newStatus = isActive ? "0" : "1";
    const ok = await onStatusToggle(c.CONGRESSID, newStatus);
    if(ok) setStatus(newStatus);
    setToggling(false);
  };

  return (
    <div onClick={() => onSelect(c)}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        background: "#fff",
        borderTop: `1px solid ${hov ? cardColor : isActive ? `${color}44` : "#E5E7EB"}`,
        borderRight: `1px solid ${hov ? cardColor : isActive ? `${color}44` : "#E5E7EB"}`,
        borderBottom: `1px solid ${hov ? cardColor : isActive ? `${color}44` : "#E5E7EB"}`,
        borderLeft: `4px solid ${cardColor}`,
        borderRadius: "10px",
        cursor: "pointer",
        transition: "all .15s",
        transform: hov ? "translateY(-1px)" : "none",
        boxShadow: hov ? "0 6px 20px rgba(0,0,0,0.09)" : "0 1px 4px rgba(0,0,0,0.05)",
        opacity: isActive ? 1 : 0.75,
        display: "flex", flexDirection: "column",
      }}>
      <div style={{ padding: "14px 16px", flex: 1 }}>
        {/* Top badges row */}
        <div style={{ display:"flex", alignItems:"center", gap:"6px",
          marginBottom:"8px", flexWrap:"wrap" }}>
          <span style={{ fontSize:"11px", fontWeight:"600", color: isActive ? color : "#6B7280",
            background: isActive ? `${color}12` : "#F3F4F6",
            padding:"2px 9px", borderRadius:"20px", letterSpacing:".1px" }}>
            {c.ACRONYMS}
          </span>
          {!isActive && (
            <span style={{ fontSize:"10px", color:"#6B7280",
              background:"#F3F4F6", padding:"2px 8px", borderRadius:"20px",
              border:"1px solid #E5E7EB" }}>Inactive</span>
          )}
          {isCheckedIn && (
            <span style={{ fontSize:"10px", fontWeight:"600", color:"#0F6E56",
              background:"#D1FAE5", padding:"2px 8px", borderRadius:"20px",
              display:"flex", alignItems:"center", gap:"3px" }}>
              <span style={{ width:"5px", height:"5px", borderRadius:"50%",
                background:"#10B981", flexShrink:0 }}/>
              Checked in
            </span>
          )}
        </div>

        {/* Congress name */}
        <div style={{ fontSize:"15px", fontWeight:"600", lineHeight:1.3,
          color: isActive ? "#111" : "#6B7280", marginBottom:"10px" }}>
          {c.CONGRESSNAME}
        </div>

        {/* Date */}
        {fmtDateRange(c) && (
          <div style={{ display:"flex", alignItems:"center", gap:"7px",
            fontSize:"12px", color:"#6B7280", marginBottom:"5px" }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            {fmtDateRange(c)}
          </div>
        )}

        {/* Location */}
        {(c.CITY || c.LOCATION) && (
          <div style={{ display:"flex", alignItems:"center", gap:"7px",
            fontSize:"12px", color:"#6B7280", marginBottom:"10px" }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
            {[c.LOCATION, c.CITY, c.COUNTRYNAME].filter(Boolean).join(", ")}
          </div>
        )}
      </div>

      {/* Footer — stats + admin toggle */}
      <div style={{ padding:"8px 16px", borderTop:`1px solid ${isActive?`${color}18`:"#F3F4F6"}`,
        display:"flex", gap:"8px", alignItems:"center", flexWrap:"wrap" }}>
        {isAdmin && (
          <button onClick={toggleStatus} disabled={toggling}
            style={{ fontSize:"10px", fontWeight:"600", padding:"2px 9px",
              borderRadius:"20px", border:"none", cursor:toggling?"not-allowed":"pointer",
              background:isActive?"#FEE2E2":"#D1FAE5",
              color:isActive?"#991B1B":"#065F46",
              opacity:toggling?0.6:1, transition:"all .15s",
              fontFamily:"inherit" }}>
            {toggling ? "…" : isActive ? "Deactivate" : "Activate"}
          </button>
        )}
        {(!!c.UPLOADS || !!c.REPORTS) && (
          <>
          {!!c.UPLOADS && (
            <span style={{ fontSize:"11px", fontWeight:"500",
              color: isActive ? color : "#6B7280",
              background: isActive ? `${color}10` : "#F3F4F6",
              padding:"2px 8px", borderRadius:"6px",
              display:"flex", alignItems:"center", gap:"4px" }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5">
                <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
              </svg>
              {c.UPLOADS} uploads
            </span>
          )}
          {!!c.REPORTS && (
            <span style={{ fontSize:"11px", fontWeight:"500",
              color:"#534AB7", background:"#EEEDFE",
              padding:"2px 8px", borderRadius:"6px",
              display:"flex", alignItems:"center", gap:"4px" }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="20" x2="18" y2="10"/>
                <line x1="12" y1="20" x2="12" y2="4"/>
                <line x1="6" y1="20" x2="6" y2="14"/>
              </svg>
              {c.REPORTS} reports
            </span>
          )}
          </>
        )}
      </div>
    </div>
  );
}

export default function CongressScreen({ user, subproject: activeSub,
  initCongressId, initPresentationId, initDocId, initDoctypeId, initCommentId }) {
  const ctx     = useContext(ThemeCtx) || {};
  const color   = ctx.theme?.primary || C.primary;
  const isAdmin = activeSub?.ROLE === 1;

  const [congresses, setCongresses] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [filter,     setFilter]     = useState(initCongressId ? "all" : "active");
  const [search,     setSearch]     = useState("");
  const [selected,   setSelected]   = useState(null);

  const setCongressStatus = async(congressId, newStatus) => {
    try {
      const fd = new FormData();
      fd.append("congressid",     congressId);
      fd.append("subprojectid",   activeSub.SUBPROJECTID);
      fd.append("userid",         user.USERID);
      fd.append("congress_status",newStatus);
      const r = await fetch(`${API}/set_congress_status`,
        {method:"POST", headers:{"Auth-Code":user.AUTH_CODE}, body:fd});
      const res = await r.json();
      return res.STATUS==="1"||res.STATUS===1;
    } catch(e) { return false; }
  };

  const load = async (status) => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ userid: user.USERID, subprojectid: activeSub.SUBPROJECTID });
      if (status === "active")   p.append("congress_status", "1");
      if (status === "inactive") p.append("congress_status", "0");
      const r = await fetch(`${API}/select_conference?${p}`,
        { headers: { "Auth-Code": user.AUTH_CODE } });
      const res = await r.json();
      if (String(res.STATUS)==="1"&&Array.isArray(res.DATA)) setCongresses(res.DATA);
      else setCongresses([]);
    } catch(e) { setCongresses([]); }
    setLoading(false);
  };

  useEffect(() => { if(user&&activeSub) load(filter); }, [filter, activeSub?.SUBPROJECTID]);

  // auto-open congress from URL params once list loads
  useEffect(() => {
    if(!initCongressId || loading || congresses.length===0 || selected) return;
    const match = congresses.find(c => String(c.CONGRESSID) === String(initCongressId));
    if(match) {
      sessionStorage.removeItem("pv_congress_init"); // clear so back nav doesn't re-trigger
      setSelected(match);
    }
  }, [loading, congresses]);

  if (selected) return (
    <div style={{ position:"fixed", top:"48px", left:"200px",
      right:0, bottom:0, zIndex:900,
      background:"#fff", display:"flex", flexDirection:"column", overflow:"hidden" }}>
      <CongressPlannerInline congress={selected} user={user}
        subproject={activeSub} color={color} isAdmin={isAdmin}
        onBack={() => setSelected(null)}
        initPresentationId={initPresentationId}
        initDocId={initDocId}
        initDoctypeId={initDoctypeId}
        initCommentId={initCommentId}/>
    </div>
  );

  const filtered = congresses.filter(c =>
    !search ||
    (c.CONGRESSNAME||"").toLowerCase().includes(search.toLowerCase()) ||
    (c.ACRONYMS||"").toLowerCase().includes(search.toLowerCase()) ||
    (c.CITY||"").toLowerCase().includes(search.toLowerCase())
  );

  // Separate active and inactive for display
  const active   = filtered.filter(c => String(c.CONGRESS_STATUS)==="1");
  const inactive = filtered.filter(c => String(c.CONGRESS_STATUS)!=="1");

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%",
      overflow:"hidden", background:"#F8FAFC" }}>
      {/* Header */}
      <div style={{ flexShrink:0, background:color, padding:"18px 24px 14px", color:"#fff" }}>
        <div style={{ fontSize:"10px", opacity:.7, letterSpacing:".6px",
          textTransform:"uppercase", marginBottom:"3px" }}>
          {activeSub?.SUBPROJECTNAME}
        </div>
        <div style={{ fontSize:"22px", fontWeight:"600", letterSpacing:"-.3px" }}>Congress</div>
      </div>

      {/* Search + filters */}
      <div style={{ flexShrink:0, padding:"12px 20px",
        background:"#fff", borderBottom:"1px solid #E5E7EB",
        display:"flex", gap:"10px", alignItems:"center", flexWrap:"wrap" }}>
        <div style={{ flex:1, minWidth:"200px", display:"flex", alignItems:"center",
          gap:"8px", background:"#F3F4F6", border:"1px solid #E5E7EB",
          borderRadius:"8px", padding:"7px 12px" }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
            stroke="#9CA3AF" strokeWidth="2.5">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <input value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Name, acronym, city…"
            style={{ flex:1, border:"none", background:"transparent",
              fontSize:"13px", outline:"none", fontFamily:"inherit", color:"#111" }}/>
          {search && <button onClick={()=>setSearch("")}
            style={{ background:"none", border:"none", cursor:"pointer",
              color:"#9CA3AF", fontSize:"16px", lineHeight:1, padding:0 }}>×</button>}
        </div>
        {isAdmin && (
          <div style={{ display:"flex", gap:"4px", background:"#F3F4F6",
            padding:"3px", borderRadius:"8px", border:"1px solid #E5E7EB" }}>
            {[{id:"active",l:"Active"},{id:"all",l:"All"},{id:"inactive",l:"Inactive"}].map(f=>(
              <button key={f.id} onClick={()=>setFilter(f.id)}
                style={{ padding:"5px 14px", borderRadius:"6px", cursor:"pointer",
                  fontSize:"12px", fontWeight:"500",
                  border:"none",
                  background: filter===f.id ? color : "transparent",
                  color: filter===f.id ? "#fff" : "#6B7280",
                  fontFamily:"inherit", transition:"all .15s" }}>
                {f.l}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Grid */}
      <div style={{ flex:1, overflowY:"auto", padding:"20px 24px" }}>
        {loading && (
          <div style={{ textAlign:"center", padding:"80px 20px",
            color:"#9CA3AF", fontSize:"13px" }}>Loading congresses…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div style={{ textAlign:"center", padding:"80px 20px" }}>
            <div style={{ fontSize:"13px", color:"#9CA3AF", marginBottom: search?"10px":"0" }}>
              {search ? `No results for "${search}"` : "No congresses available"}
            </div>
            {search && <button onClick={()=>setSearch("")}
              style={{ fontSize:"12px", color, background:"none", border:"none",
                cursor:"pointer", fontFamily:"inherit" }}>Clear search</button>}
          </div>
        )}

        {/* Active congresses */}
        {!loading && active.length > 0 && (
          <>
            {filter==="all" && (
              <div style={{ fontSize:"11px", fontWeight:"600", color:"#374151",
                letterSpacing:".4px", textTransform:"uppercase",
                marginBottom:"10px", display:"flex", alignItems:"center", gap:"8px" }}>
                <span style={{ width:"8px", height:"8px", borderRadius:"50%",
                  background:"#10B981", flexShrink:0 }}/>
                Active · {active.length}
              </div>
            )}
            <div style={{ display:"grid", gap:"12px",
              gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",
              marginBottom: inactive.length>0?"24px":"0" }}>
              {active.map(c => (
                <CongressCard key={c.CONGRESSID} congress={c}
                  color={color} onSelect={setSelected}
                  isAdmin={isAdmin}
                  onStatusToggle={setCongressStatus}/>
              ))}
            </div>
          </>
        )}

        {/* Inactive congresses */}
        {!loading && inactive.length > 0 && (
          <>
            {filter==="all" && (
              <div style={{ fontSize:"11px", fontWeight:"600", color:"#9CA3AF",
                letterSpacing:".4px", textTransform:"uppercase",
                marginBottom:"10px", display:"flex", alignItems:"center", gap:"8px" }}>
                <span style={{ width:"8px", height:"8px", borderRadius:"50%",
                  background:"#D1D5DB", flexShrink:0 }}/>
                Inactive · {inactive.length}
              </div>
            )}
            <div style={{ display:"grid", gap:"12px",
              gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))" }}>
              {inactive.map(c => (
                <CongressCard key={c.CONGRESSID} congress={c}
                  color={color} onSelect={setSelected}
                  isAdmin={isAdmin}
                  onStatusToggle={setCongressStatus}/>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}